import { FinWordStats, QuizStats, QuizQuestion, QuizCategory } from "./types";
import { getDaysBetween } from "./dateUtils";

/**
 * A streak only survives consecutive days: if more than one day passed since
 * the last recorded daily game, the old chain is dead and today's result
 * starts a new one. Legacy stats without lastPlayedDate are grandfathered
 * (no reset) the first time through.
 */
function staleStreakReset(currentStreak: number, lastPlayedDate: string | undefined, playedDate: string): number {
  if (!lastPlayedDate) return currentStreak;
  return getDaysBetween(lastPlayedDate, playedDate) > 1 ? 0 : currentStreak;
}

// Two-pass algorithm for duplicate letter matching
export function getLetterStatuses(guess: string, solution: string): Array<"correct" | "present" | "absent"> {
  const statuses = new Array(5).fill("absent") as Array<"correct" | "present" | "absent">;
  const targetLetters = solution.split("");

  // Pass 1: exact matches (green)
  for (let i = 0; i < 5; i++) {
    if (guess[i] === solution[i]) {
      statuses[i] = "correct";
      targetLetters[i] = "";
    }
  }

  // Pass 2: misplaced matches (yellow)
  for (let i = 0; i < 5; i++) {
    if (statuses[i] === "correct") continue;
    const index = targetLetters.indexOf(guess[i]);
    if (index !== -1) {
      statuses[i] = "present";
      targetLetters[index] = "";
    } else {
      statuses[i] = "absent";
    }
  }

  return statuses;
}


// 1. FinWord Stats Updater
export function updateFinWordStats(currentStats: FinWordStats, won: boolean, guessCount: number, playedDate: string): FinWordStats {
  const gamesPlayed = currentStats.gamesPlayed + 1;
  const gamesWon = currentStats.gamesWon + (won ? 1 : 0);
  
  let currentStreak = staleStreakReset(currentStats.currentStreak, currentStats.lastPlayedDate, playedDate);
  if (won) {
    currentStreak += 1;
  } else {
    currentStreak = 0;
  }
  
  const maxStreak = Math.max(currentStats.maxStreak, currentStreak);
  
  const guessDistribution = [...currentStats.guessDistribution];
  if (won && guessCount >= 1 && guessCount <= 6) {
    guessDistribution[guessCount - 1] += 1;
  }
  
  // Calculate average guesses for won games
  let totalGuessesOfWins = 0;
  let wonGamesCount = 0;
  for (let i = 0; i < 6; i++) {
    totalGuessesOfWins += guessDistribution[i] * (i + 1);
    wonGamesCount += guessDistribution[i];
  }
  const averageGuesses = wonGamesCount > 0 ? Number((totalGuessesOfWins / wonGamesCount).toFixed(2)) : 0;
  
  return {
    gamesPlayed,
    gamesWon,
    currentStreak,
    maxStreak,
    guessDistribution,
    averageGuesses,
    lastPlayedDate: playedDate,
  };
}

// 2. Quiz Stats Updater
export function updateQuizStats(
  currentStats: QuizStats,
  questions: readonly QuizQuestion[],
  answers: Record<string, number>,
  playedDate: string
): QuizStats {
  const quizzesTaken = currentStats.quizzesTaken + 1;
  const totalQuestions = currentStats.totalQuestions + questions.length;
  
  let correctCount = 0;
  const categoryCorrect: Record<string, number> = {};
  const categoryTotal: Record<string, number> = {};
  
  questions.forEach((q) => {
    const userAnswer = answers[q.id];
    const isCorrect = userAnswer === q.correctIndex;
    if (isCorrect) {
      correctCount += 1;
    }
    
    const cat = q.category;
    categoryTotal[cat] = (categoryTotal[cat] || 0) + 1;
    if (isCorrect) {
      categoryCorrect[cat] = (categoryCorrect[cat] || 0) + 1;
    }
  });
  
  const correctAnswers = currentStats.correctAnswers + correctCount;
  const scorePercent = (correctCount / questions.length) * 100;
  
  // Streak increments only on 75%+ score (3+ out of 4)
  const passed = scorePercent >= 75;
  let currentStreak = staleStreakReset(currentStats.currentStreak, currentStats.lastPlayedDate, playedDate);
  if (passed) {
    currentStreak += 1;
  } else {
    currentStreak = 0;
  }
  const maxStreak = Math.max(currentStats.maxStreak, currentStreak);
  
  const averageScore = Number(
    ((currentStats.averageScore * (quizzesTaken - 1) + scorePercent) / quizzesTaken).toFixed(2)
  );
  
  // Update category performance
  const categoryPerformance = { ...currentStats.categoryPerformance } as Record<QuizCategory, number>;
  const categories: QuizCategory[] = ["tax", "investing", "loans", "savings", "retirement", "budgeting", "general"];
  
  categories.forEach((cat) => {
    // We add the new attempts to category scores
    if (categoryTotal[cat]) {
      const prevScore = categoryPerformance[cat] || 0;
      // We assume historical weight based on total questions taken so far
      // For simplicity, let's mix the new correct rate into the category performance
      const totalInCat = categoryTotal[cat];
      const correctInCat = categoryCorrect[cat] || 0;
      const dayRate = (correctInCat / totalInCat) * 100;
      
      // Calculate a moving average
      if (prevScore === 0) {
        categoryPerformance[cat] = dayRate;
      } else {
        categoryPerformance[cat] = Number(((prevScore * 4 + dayRate) / 5).toFixed(2));
      }
    }
  });
  
  return {
    quizzesTaken,
    totalQuestions,
    correctAnswers,
    currentStreak,
    maxStreak,
    averageScore,
    categoryPerformance: categoryPerformance as Readonly<Record<QuizCategory, number>>,
    lastPlayedDate: playedDate,
  };
}
